/**
 * The face which goes beside a name.
 *
 * Every avatar on every page is drawn by this portal out of the skin the
 * account is wearing, and the address is the nickname rather than anything
 * about skins - which is what lets a list of names put a face in front of
 * each of them without knowing that skins exist.
 *
 * The sizes are the ones the server actually draws. A number nobody asks for
 * is answered at the size it hands out anyway, so asking for one only means
 * the browser scales a picture it did not need to scale.
 */

/** How large a face is drawn beside a name in a list. */
export const ROW_FACE = 20;

/** Return where the face of `username` is drawn. */
export function faceUrl(username: string, size: number = ROW_FACE): string {
    return `/skins/head/${encodeURIComponent(username || "-")}.png?s=${size}`;
}

/**
 * Return the picture of the face of `username`.
 *
 * The shape is written onto the element rather than left to the stylesheet:
 * a face which arrives after the row around it has been measured is a row
 * which jumps, and these are drawn in lists a dozen rows long.
 */
export function faceImage(username: string, size: number = ROW_FACE): HTMLImageElement {
    const image = document.createElement("img");
    image.className = "avatar";
    image.src = faceUrl(username, size);
    image.width = size;
    image.height = size;
    image.alt = "";
    image.loading = "lazy";
    image.setAttribute("aria-hidden", "true");

    return image;
}

/**
 * Return the face of `username` with a dot saying whether they are about.
 *
 * The dot is drawn over the corner of the picture rather than beside it, so a
 * row which already holds a face, a name and a time does not have to find
 * room for a fourth thing - and the answer ends up on the one part of the
 * line somebody is already looking at.
 *
 * Green for here and grey for not: two states rather than the three the badge
 * on a profile carries. In a conversation "we have never seen them" and "not
 * here" are the same answer, and a dot has nowhere to put the difference.
 */
export function faceWithPresence(username: string, size: number, isOnline: boolean): HTMLElement {
    const holder = document.createElement("span");
    holder.className = "face-presence";

    const dot = document.createElement("span");
    dot.className = isOnline ? "face-presence__dot face-presence__dot--online" : "face-presence__dot";
    dot.setAttribute("aria-hidden", "true");

    holder.append(faceImage(username, size), dot);

    return holder;
}
